/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useMutation } from 'react-apollo';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { useSessionData } from '@confluence/session-data';
import { usePageContentId } from '@confluence/page-context';
import {
	ResolvedCommentsQuery,
	type GraphQLContentStatus,
	type ResolvedCommentsQueryType,
	type ResolvedCommentsQueryVariables,
	type ConfluenceCommentResolveAllLocation,
} from '@confluence/comments-panel-queries';

import type {
	useCommentBulkResolveResolveAllCommentsMutation as useCommentBulkResolveResolveAllCommentsMutation$data,
	useCommentBulkResolveResolveAllCommentsMutationVariables as useCommentBulkResolveResolveAllCommentsMutation$variables,
} from './__types__/useCommentBulkResolveResolveAllCommentsMutation';
import { useCommentBulkOperationsActions } from './useCommentBulkOperations';
import { useSyncResolvedComments } from './useSyncResolvedComments';
import type { Flags } from './useCommentBulkOperationsFlags';
import { useLocation } from './useLocation';

const useCommentBulkResolveResolveAllCommentsMutation = gql`
	mutation useCommentBulkResolveResolveAllCommentsMutation(
		$contentId: ID!
		$cloudId: ID!
		$resolveView: ConfluenceCommentResolveAllLocation = RENDERER
	) {
		confluence_resolveCommentsByContentId(
			contentId: $contentId
			cloudId: $cloudId
			resolveView: $resolveView
		) {
			success
			errors {
				message
			}
		}
	}
`;
export const useCommentBulkResolve = (flags: Flags) => {
	const [contentId = ''] = usePageContentId();
	const { cloudId } = useSessionData();
	const location = useLocation();
	const { setIsBulkResolveInProgress } = useCommentBulkOperationsActions();
	const syncResolvedComments = useSyncResolvedComments();

	const handleResolveAllComments = () => {
		void resolveAllComments({
			variables: {
				contentId,
				cloudId,
				resolveView: location as ConfluenceCommentResolveAllLocation,
			},
		});

		setIsBulkResolveInProgress(true);
	};

	const [resolveAllComments] = useMutation<
		useCommentBulkResolveResolveAllCommentsMutation$data,
		useCommentBulkResolveResolveAllCommentsMutation$variables
	>(useCommentBulkResolveResolveAllCommentsMutation, {
		onCompleted: async ({ confluence_resolveCommentsByContentId }) => {
			const client = getApolloClient();
			const { data: resolvedCommentsData } = await client.query<
				ResolvedCommentsQueryType,
				ResolvedCommentsQueryVariables
			>({
				query: ResolvedCommentsQuery,
				variables: {
					pageId: contentId,
					contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
				},
				fetchPolicy: 'network-only',
			});
			syncResolvedComments(resolvedCommentsData);
			if (confluence_resolveCommentsByContentId?.success) {
				flags.showSuccess();
			} else {
				flags.showFailure(handleResolveAllComments);
			}
			setIsBulkResolveInProgress(false);
		},
		onError: () => {
			flags.showFailure(handleResolveAllComments);
			setIsBulkResolveInProgress(false);
		},
	});
	return handleResolveAllComments;
};
